import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { z } from 'zod';

import { columnsFor, type ColumnSpec } from './columns';
import type { DataViewBlockProps } from './data-view-block';
import type { StatusMap } from './status';

/**
 * Linhas de um arquivo CNAB (F3b, `components/FileLines` do legado): `remessa` é a
 * `FileInstruction` (o que a UY3 enviou ao banco) e `retorno` a `FileConfirmation` (o que o banco
 * devolveu). Só a remessa tem status da operação e erro; só o retorno tem recibo.
 */
export type FileLinesKind = 'remessa' | 'retorno';

const text = z.string().nullish();
const num = z.number().nullish();

/** `IDetailFileLine` do legado (`services/walletManagement/bankSlip/bankSlip.types.ts:108-147`). */
export const fileLineDetailSchema = z.looseObject({
  bankSlipId: text,
  barCode: text,
  numericLine: text,
  confirmationTransactionTypeValue: text,
  confirmationTransactionTypeValueDisplay: text,
  documentNumber: text,
  participantControlNumber: z.union([z.number(), z.string()]).nullish(),
  deduction: num,
  discount: num,
  payerName: text,
  amount: num,
  ourNumber: z.union([z.number(), z.string()]).nullish(),
  ourNumberDigit: text,
  walletNumber: text,
  dueDate: text,
  fineTypeValue: text,
  fineTypeValueDisplay: text,
  fineDecimal: num,
  statusOperationValue: text,
  statusOperationValueDisplay: text,
  error: text,
  createdAt: text,
});

/** `LinesFileTypeGeneric` do legado (`bankSlip.types.ts:100-106`): o item de `GET …/Lines`. */
export const fileLineSchema = z.looseObject({
  id: z.string(),
  lineType: text,
  walletCode: z.union([z.string(), z.number()]).nullish(),
  sequentialNumber: num,
  detail: fileLineDetailSchema.nullish(),
});

export type FileLine = z.infer<typeof fileLineSchema>;
export type FileLineDetail = z.infer<typeof fileLineDetailSchema>;

/**
 * Status da operação na remessa, com os tons da U19 pela cor do legado
 * (`components/FileLines/ShowOperationStatus.tsx:16-26`): verde = `ok`, amarelo = `warn` (aguardando
 * a CIP), vermelho = `err`. `Desconhecida` era azul no legado e vira `neutral`. Os rótulos saem do
 * valor técnico (U33); valor fora do mapa mostra o `statusOperationValueDisplay` da API.
 */
export const FILE_LINE_STATUS: StatusMap = {
  Registrada: { label: msg`Registrada`, tone: 'ok', icon: 'circle-check' },
  ProcessadaJd: { label: msg`Processada JD`, tone: 'ok', icon: 'circle-check' },
  ProcessadoUY3: { label: msg`Processado UY3`, tone: 'ok', icon: 'circle-check' },
  InformacaoCip: { label: msg`Informação CIP`, tone: 'warn', icon: 'info' },
  AguardandoCip: { label: msg`Aguardando CIP`, tone: 'warn', icon: 'hourglass' },
  Desconhecida: { label: msg`Desconhecida`, tone: 'neutral', icon: 'circle-dashed' },
  ErroJd: { label: msg`Erro JD`, tone: 'err', icon: 'circle-x' },
  ErroCip: { label: msg`Erro CIP`, tone: 'err', icon: 'circle-x' },
  ErroUy3: { label: msg`Erro UY3`, tone: 'err', icon: 'circle-x' },
};

const col = columnsFor<FileLine>();

/**
 * Colunas da lista, na ordem do legado (`FileLinesList.tsx:36-122`): Instrução, Número do documento,
 * Pagador, Criado em e, só na remessa, Status. No iPhone, o pagador é o título do cartão.
 */
export function fileLinesColumns(kind: FileLinesKind): readonly ColumnSpec[] {
  return [
    col.text('detail.confirmationTransactionTypeValueDisplay', msg`Instrução`, { mobile: 'meta' }),
    col.text('detail.documentNumber', msg`Número do documento`, { mobile: 'subtitle' }),
    col.text('detail.payerName', msg`Pagador`, { mobile: 'title' }),
    col.date('detail.createdAt', msg`Criado em`, { mobile: 'meta' }),
    ...(kind === 'remessa'
      ? [col.status('detail.statusOperationValue', msg`Status`, FILE_LINE_STATUS, { fallbackField: 'detail.statusOperationValueDisplay' })]
      : []),
  ];
}

/** Ids das intenções que a lista pede ao hospedeiro (U41): detalhe, recibo (retorno) e página. */
export interface FileLinesActions {
  /** "Ver detalhes": `{ id, name }` da linha. */
  readonly details: string;
  /** "Download Recibo", só no retorno (`FileLinesList.tsx:111-117`): `{ id, name }` da linha. */
  readonly receipt?: string;
  /** Página: `{ page, size }` (página a partir de 0). */
  readonly page: string;
}

/**
 * Props do `DataViewBlock` para as linhas: `defineConfigBlock(DataViewBlock)({ …, props: (p) =>
 * fileLinesBlockProps('remessa', p, { details, page }) })`. Sem filtro e sem ordenação, como o legado
 * (`FileLinesList.tsx:126-129`).
 */
export function fileLinesBlockProps(
  kind: FileLinesKind,
  params: { readonly page: number; readonly size: number },
  actions: FileLinesActions,
): DataViewBlockProps {
  return {
    columns: [...fileLinesColumns(kind)],
    rowKey: 'id',
    label: fileLinesTitle(kind),
    page: params.page,
    size: params.size,
    pageAction: actions.page,
    openAction: actions.details,
    rowActions: [
      { id: actions.details, label: msg`Ver detalhes`, icon: 'eye' },
      ...(kind === 'retorno' && actions.receipt ? [{ id: actions.receipt, label: msg`Download Recibo`, icon: 'download' as const }] : []),
    ],
  };
}

/** Título da tela (`FileLinesHeader.tsx:26`: "Transações do arquivo {remessa|retorno}"). */
export function fileLinesTitle(kind: FileLinesKind): MessageDescriptor {
  return kind === 'remessa' ? msg`Transações do arquivo remessa` : msg`Transações do arquivo retorno`;
}

/**
 * Query de `GET /FileInstruction/{id}/Lines` e `GET /FileConfirmation/{id}/Lines` (BillingApi) como o
 * legado monta (`FileLinesContainer.tsx:47-51` + `fileInstructionContext.tsx:139-147`): a remessa leva
 * `lineType=RemessaTransacao` **e** o `lineTypes=RetornoTransacao` que o contêiner põe para os dois.
 */
/* eslint-disable lingui/no-unlocalized-strings -- valores do fio, não texto de UI */
export function legacyFileLinesParams(kind: FileLinesKind, page: { readonly page: number; readonly size: number }) {
  const shared = { lineTypes: 'RetornoTransacao', page: page.page, size: page.size };
  return kind === 'remessa' ? { lineType: 'RemessaTransacao', ...shared } : shared;
}
/* eslint-enable lingui/no-unlocalized-strings */
